import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Container, Card, Heading, Paragraph } from '@scentregroup/ui'
import classNames from 'classnames'
import AutoPotentialLink from '../auto-potential-link'
import Article from '../article'
import RetailerUtilityBox from '../../components/retailer-utility-box'
import { Markdown } from '../../hub-components/markdown'
import CentreUtilityBox from '../centre-utility-box'
import { CustomThemeContext } from '../../context'
import ResponsiveAsset from '../responsive-asset'
import { matchMedia } from '../../helpers/match-media'
import Trackable from '../trackable'
import { trackingActions, trackingTypes } from '../../constants'
import { occurrencesType, callToActionType } from './../../types'
import style from './index.module.scss'
import wMaskStyle from './w-mask.module.scss'
import WMask from './w-mask.inline.svg'

const MAXIMUM_MASK_LOAD_TIME = 3500

function useManageMask(withMask) {
  const [showMask, setShowMask] = useState(withMask)
  const [animateMask, setAnimateMask] = useState(false)
  const hideMask = useCallback(() => {
    setShowMask(false)
    setAnimateMask(false)
  }, [])
  const maskLoadTimeout = useRef(null)
  useEffect(() => {
    maskLoadTimeout.current = setTimeout(hideMask, MAXIMUM_MASK_LOAD_TIME)
  }, [hideMask])
  const showMaskQuery = withMask ? matchMedia('(orientation: portrait)') : null
  const startMaskAnimation = useCallback(() => {
    if (maskLoadTimeout.current) {
      clearTimeout(maskLoadTimeout.current)
    }
    if (showMaskQuery?.matches) {
      setAnimateMask(true)
    } else {
      hideMask()
    }
  }, [hideMask, showMaskQuery])

  const maskAnimationDone = useCallback(
    event => {
      if (
        event.animationName === wMaskStyle.maskAnimZoomIn ||
        event.animationName === wMaskStyle.maskAnimFadeOut
      ) {
        hideMask()
      }
    },
    [hideMask]
  )
  const onLoadHandleMask = withMask ? startMaskAnimation : null
  const onErrorHandleMask = withMask ? hideMask : null
  return {
    showMask,
    animateMask,
    maskAnimationDone,
    onLoadHandleMask,
    onErrorHandleMask,
  }
}

const Feature = ({
  position,
  topic,
  title,
  body,
  entryType,
  desktopImageUrl,
  mobileImageUrl,
  videoUrl,
  link,
  location,
  occurrences,
  callsToAction,
  withMask,
  priceInfo,
  infoAccordion,
  pastEventsAccordion,
  utilityPanelModule,
  shareButtonType,
  analyticsMetadata,
  centred = false,
  terms = '',
  retailerUtility = null,
  lazyLoad = true,
}) => {
  const {
    showMask,
    animateMask,
    maskAnimationDone,
    onLoadHandleMask,
    onErrorHandleMask,
  } = useManageMask(withMask)

  const ariaLabel = topic ? `${topic} - ${title}` : title
  return (
    <Trackable
      trackingAction={trackingActions.viewPromotion}
      type={trackingTypes.promotion}
      {...analyticsMetadata}
    >
      <CustomThemeContext.Consumer>
        {themeOption => (
          <div className={classNames(style.featureWrapper, style.evolved)}>
            <div
              className={classNames(style.Feature, {
                [style.placeholder]: !desktopImageUrl || !mobileImageUrl,
                [style.Grey]: themeOption.luxury,
                [wMaskStyle.maskedContainer]: showMask,
                [wMaskStyle.moveTheMask]: showMask && animateMask,
              })}
              onAnimationEnd={maskAnimationDone}
            >
              {Boolean(desktopImageUrl && mobileImageUrl) && (
                <AutoPotentialLink link={link} aria-label={ariaLabel}>
                  <ResponsiveAsset
                    angleOrigin={null}
                    className={classNames(style.evolvedAsset, {
                      [wMaskStyle.maskedImage]: showMask,
                    })}
                    desktopImageUrl={desktopImageUrl}
                    mobileImageUrl={mobileImageUrl}
                    videoUrl={videoUrl}
                    mobileRatio="square"
                    desktopRatio="square"
                    numberOfItems={1}
                    alt={title}
                    lazyLoad={lazyLoad}
                    dataTestId="featured-image"
                    onLoad={onLoadHandleMask}
                    onError={onErrorHandleMask}
                  />
                </AutoPotentialLink>
              )}
              <Container
                centred
                type="large"
                className={classNames(style.Container, style[position], {
                  [wMaskStyle.maskedContent]: showMask,
                })}
              >
                <Card
                  className={classNames(style.Card, {
                    [style.centred]: centred,
                  })}
                >
                  <AutoPotentialLink
                    className={style.addLinkStyles}
                    link={link}
                    aria-label={ariaLabel}
                  >
                    {entryType && (
                      <Heading
                        type="extrasmall"
                        className={style.superHeading}
                        data-test-id="featured-type"
                      >
                        {entryType}
                      </Heading>
                    )}
                    <Article
                      shareButtonType={shareButtonType}
                      superheading={topic}
                      heading={title}
                      headingType="large"
                      location={location}
                      occurrences={occurrences}
                      callsToAction={callsToAction}
                      infoAccordion={infoAccordion}
                      pastEventsAccordion={pastEventsAccordion}
                      priceInfo={priceInfo}
                      headerDataTestId="featured-type"
                      dataTestId="featured-title"
                    >
                      <Markdown
                        content={body}
                        customRenderers={() => ({
                          paragraph: ({ children }) => (
                            <Paragraph className={style.articleParagraph}>
                              {children}
                            </Paragraph>
                          ),
                        })}
                      />
                      {terms && <Markdown content={terms} />}
                    </Article>
                  </AutoPotentialLink>
                </Card>
                {utilityPanelModule && (
                  <div className={style.Sidebar}>
                    <CentreUtilityBox
                      {...utilityPanelModule}
                      origin={'feature'}
                    />
                  </div>
                )}
                {retailerUtility && (
                  <div className={style.Sidebar}>
                    <RetailerUtilityBox {...retailerUtility} />
                  </div>
                )}
              </Container>
              {showMask && <WMask className={wMaskStyle.wDeviceMask} />}
            </div>
          </div>
        )}
      </CustomThemeContext.Consumer>
    </Trackable>
  )
}

Feature.propTypes = {
  position: PropTypes.oneOf(['left', 'right']),
  centred: PropTypes.bool,
  topic: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  entryType: PropTypes.string,
  terms: PropTypes.string,
  desktopImageUrl: PropTypes.string,
  mobileImageUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  link: PropTypes.string,
  retailerUtility: PropTypes.object,
  location: PropTypes.string,
  occurrences: occurrencesType,
  lazyLoad: PropTypes.bool,
  callsToAction: callToActionType,
  withMask: PropTypes.bool,
  priceInfo: PropTypes.string,
  infoAccordion: PropTypes.array,
  pastEventsAccordion: PropTypes.array,
  utilityPanelModule: PropTypes.object,
  shareButtonType: PropTypes.string,
  analyticsMetadata: PropTypes.shape({
    correlationId: PropTypes.string,
    title: PropTypes.string,
    promotion: PropTypes.shape({
      creative: PropTypes.string,
    }),
  }),
}

export default Feature
